/**
 * App top failure & notification banners — TS-060 (DEC-COUNCIL-015).
 *
 * Renders transient top notices (e.g. quote duplicated) and persistent
 * save failure banners with retry / recovery actions.
 */

import React from 'react';
import { Icons } from './Icon';

export interface TopNoticeState {
    kind?: 'info' | 'success';
    message: string;
}

export interface SaveErrorState {
    kind?: 'quota' | 'offline' | 'auth' | 'backend' | 'conflict' | 'aborted' | 'unknown';
    message: string;
    detail?: string;
}

export interface AppTopBannersProps {
    topNotice: TopNoticeState | null;
    onDismissTopNotice: () => void;
    saveError: SaveErrorState | null;
    onDismissSaveError: () => void;
    isSavingProject: boolean;
    onRetrySave: () => void;
    onNavigateToQuotes: () => void;
}

export const AppTopBanners: React.FC<AppTopBannersProps> = ({
    topNotice,
    onDismissTopNotice,
    saveError,
    onDismissSaveError,
    isSavingProject,
    onRetrySave,
    onNavigateToQuotes,
}) => {
    return (
        <>
            {/* Top success/info ribbon: transient confirmation (e.g. project duplicated) */}
            {topNotice && (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        pointerEvents: 'auto',
                        background: topNotice.kind === 'info'
                            ? 'linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%)'
                            : 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)',
                        borderBottom: `2px solid ${topNotice.kind === 'info' ? '#1d4ed8' : '#15803d'}`,
                        color: topNotice.kind === 'info' ? '#1e3a8a' : '#14532d',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        boxShadow: `0 2px 8px ${topNotice.kind === 'info' ? 'rgba(29,78,216,0.22)' : 'rgba(21,128,61,0.22)'}`,
                    }}
                >
                    <Icons.CheckCircle size={16} style={{ color: topNotice.kind === 'info' ? '#1d4ed8' : '#15803d', flexShrink: 0 }} />
                    <div style={{ flex: 1, lineHeight: 1.4 }}>{topNotice.message}</div>
                    <button
                        type="button"
                        onClick={onDismissTopNotice}
                        aria-label="Dismiss"
                        style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px', flexShrink: 0 }}
                    >
                        <Icons.X size={16} />
                    </button>
                </div>
            )}

            {/* Persistent save-error banner with kind-specific guidance */}
            {saveError && (
                <div
                    role="alert"
                    aria-live="assertive"
                    style={{
                        pointerEvents: 'auto',
                        background: 'linear-gradient(180deg, #fee2e2 0%, #fecaca 100%)',
                        borderBottom: '2px solid #b91c1c',
                        color: '#7f1d1d',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(185, 28, 28, 0.25)',
                    }}
                >
                    <Icons.AlertTriangle size={16} style={{ color: '#991b1b', flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ flex: 1, lineHeight: 1.4 }}>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '2px' }}>
                            Save failed · {saveError.kind === 'quota' ? 'browser storage full' : saveError.kind === 'offline' ? 'no connection' : saveError.kind === 'auth' ? 'session expired' : saveError.kind === 'backend' ? 'backend error' : 'error'}
                        </div>
                        <div>{saveError.message}</div>
                        {saveError.detail && saveError.detail !== saveError.message && (
                            <details style={{ marginTop: '4px' }}>
                                <summary style={{ cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: '#7f1d1d' }}>Show technical detail</summary>
                                <pre style={{ marginTop: '4px', padding: '6px 8px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px', fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{saveError.detail}</pre>
                            </details>
                        )}
                    </div>
                    {saveError.kind === 'quota' ? (
                        <button
                            type="button"
                            onClick={onNavigateToQuotes}
                            style={{ background: '#b91c1c', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                        >
                            Open Project History
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={onRetrySave}
                            disabled={isSavingProject}
                            style={{ background: isSavingProject ? '#94a3b8' : '#b91c1c', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: isSavingProject ? 'wait' : 'pointer', flexShrink: 0 }}
                        >
                            {isSavingProject ? 'Saving…' : 'Retry Save'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onDismissSaveError}
                        style={{ background: 'transparent', color: '#7f1d1d', border: '1px solid rgba(127, 29, 29, 0.4)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                    >
                        Dismiss
                    </button>
                </div>
            )}
        </>
    );
};

export default AppTopBanners;
